/**
 * Toast store — leaf module. Transient toast notification queue.
 *
 * Moved out of store/index.js (Perf 3.1.b). The only cross-store dependency is
 * a RUNTIME auth lookup (to silently drop anon "sign in" toasts) — resolved
 * lazily so this leaf does not statically import the auth slice.
 */
import { create } from 'zustand';

// Lazy auth accessor — runtime-only (see store/modal.js for rationale).
// Points at the auth leaf directly (Perf 3.1.b Step 3).
const getAuthStore = () => require('./auth').useAuthStore;

export const MAX_VISIBLE_TOASTS = 3;
const DEFAULT_DURATION_MS = 5000;
const EARN_DURATION_MS = 7000;

let nextToastId = 0;
const dismissTimers = new Map();

function durationForType(type) {
  return type === 'earn' ? EARN_DURATION_MS : DEFAULT_DURATION_MS;
}

function clearDismissTimer(id) {
  const timer = dismissTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    dismissTimers.delete(id);
  }
}

function clearAllDismissTimers() {
  dismissTimers.forEach((timer) => clearTimeout(timer));
  dismissTimers.clear();
}

function isActionless(toastOrActions) {
  if (Array.isArray(toastOrActions)) return toastOrActions.length === 0;
  return !toastOrActions?.actions || toastOrActions.actions.length === 0;
}

function scheduleDismiss(id, duration, get) {
  clearDismissTimer(id);
  const timer = setTimeout(() => {
    dismissTimers.delete(id);
    get().actions.removeToast(id);
  }, duration);
  dismissTimers.set(id, timer);
}

export const useToastStore = create((set, get) => ({
  // State
  toasts: [],
  // Actions
  actions: {
    addToast: (message, type = 'default', actions = [], options = {}) => {
      // Public-dashboard Phase 8: anon users who slip past the Phase 6 / 7
      // gates and trigger a private endpoint get a `NO_AUTH_TOKEN` 401 from
      // `fetchWithAuth`, which surfaces as the canned "Sign in to continue."
      // string. The auth CTA flow is the source of truth for asking them to
      // sign in, so we silently drop those toasts here instead of patching
      // every caller's try/catch block.
      const isAnonAuthMessage = typeof message === 'string'
        && (message === 'Sign in to continue.' || message === 'Session expired. Please log in again.');
      if (isAnonAuthMessage) {
        const auth = getAuthStore().getState();
        const isAnon = !auth?.token || (typeof auth?.isTokenExpired === 'function' && auth.isTokenExpired());
        if (isAnon) return;
      }

      const duration = durationForType(type);
      const category = options?.category || null;

      // Repeated action-less notices (memory deleted, copied, saved, …) refresh
      // the existing toast instead of stacking until they cover the screen.
      if (isActionless(actions)) {
        const existing = get().toasts.find((toast) => (
          toast.message === message
          && toast.type === type
          && toast.category === category
          && isActionless(toast)
        ));
        if (existing) {
          scheduleDismiss(existing.id, duration, get);
          return existing.id;
        }
      }

      const id = ++nextToastId;
      const newToast = {
        id,
        message,
        type,
        actions,
        category,
      };

      set((state) => {
        let next = state.toasts;
        if (next.length >= MAX_VISIBLE_TOASTS) {
          const overflow = next.slice(0, next.length - MAX_VISIBLE_TOASTS + 1);
          overflow.forEach((toast) => clearDismissTimer(toast.id));
          next = next.slice(overflow.length);
        }
        return { toasts: [...next, newToast] };
      });

      scheduleDismiss(id, duration, get);
      return id;
    },
    removeToast: (id) => {
      clearDismissTimer(id);
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      }));
    },
    clearToasts: () => {
      clearAllDismissTimers();
      set({ toasts: [] });
    },
    removeToastsByCategory: (category) => {
      if (!category) return;
      set((state) => {
        state.toasts.forEach((toast) => {
          if (toast.category === category) clearDismissTimer(toast.id);
        });
        return {
          toasts: state.toasts.filter((t) => t.category !== category),
        };
      });
    },
  },
}));

export default useToastStore;
